import styled from "@emotion/styled";
import { Box, FormControlLabel, Switch, Typography } from "@mui/material";
import type { FC } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import type { MediaTypeManagerProps } from "./MediaTypeTabPanel/MediaTypeTabPanel";
import DataList from "@/components/DataList/DataList";
import type { DataListRow, DataListRowInternal } from "@/components/DataList/DataListRow";
import type { SearchProp } from "@/components/DataList/ListHeader/DataListHeader";
import type { DMSearchResult } from "@/declarations/models/DMSearchResult";
import type { Media } from "@/declarations/models/Media";
import { MediaType } from "@/declarations/models/MediaType";
import type { PaginationResult } from "@/declarations/PaginationResult";
import type { SelectOption } from "@/framework/KioForm/common/KioSelect";
import { KioSelect } from "@/framework/KioForm/common/KioSelect";
import { useMessenger } from "@/framework/Messenger/Messenger";
import { useDebounce } from "@/hooks/useDebounce";
import { useLookup } from "@/hooks/useLookup";
import Api from "@/services/Api";
import type { DMImage } from "@/framework/KioForm/fields/DMSelectorField";
import { Add } from "@mui/icons-material";
import type { ExpandedDmMedia } from "@/declarations/models/ExpandedDmMedia";
import { useStore } from "@/Store";
import { getDominantColor, getImageDimensions } from "@/components/FileUpload";
import { cleanDmUrl, getFileFromUrl } from "@/utils/url";

export interface SearchDMTabContentProps extends Omit<MediaTypeManagerProps, "mediaTypes"> {}

const getDmTypes = (t: TFunction): SelectOption[] => [
  { label: t("dmTab.dmTypes.allTypes"), value: "*" },
  { label: t("dmTab.dmTypes.actor"), value: "Actor" },
  { label: t("dmTab.dmTypes.person"), value: "Person" },
  { label: t("dmTab.dmTypes.organization"), value: "Organization" },
  { label: t("dmTab.dmTypes.vessel"), value: "NavalVessel" },
  { label: t("dmTab.dmTypes.photograph"), value: "Photograph" },
  { label: t("dmTab.dmTypes.thing"), value: "Thing" },
  { label: t("dmTab.dmTypes.fineArt"), value: "Fineart" },
  { label: t("dmTab.dmTypes.architecture"), value: "Architecture" },
  { label: t("dmTab.dmTypes.story"), value: "Story" },
  { label: t("dmTab.dmTypes.folder"), value: "Folder" },
  { label: t("dmTab.dmTypes.building"), value: "Building" },
  { label: t("dmTab.dmTypes.exhibition"), value: "Exhibition" },
  { label: t("dmTab.dmTypes.video"), value: "Video" },
  { label: t("dmTab.dmTypes.audio"), value: "Audio" },
];

const DmFilterContainer = styled.div`
  display: flex;
  flex-flow: row;
  gap: 16px;
  margin: 16px 16px 0;
`;

const LabelText = styled.div`
  white-space: nowrap;
`;

function mapDMSearchResultToDataListItem(dm: DMSearchResult, t: TFunction): DataListRow {
  return {
    key: String(dm.artifact_uniqueId || dm.identifier_id || dm.artifact_uuid),
    title: String(dm.artifact_ingress_title || dm.artifact_uniqueId || dm.identifier_id || "no title"),
    subTitle: dm.artifact_ingress_subjects?.join(", "),
    chipContent: dm.identifier_owner,
    imageURL: dm.thumbnail_src,
    updatedAt: dm.artifact_updatedDate,
    pictureCount: dm.artifact_pictureCount,
    supportingText: !!dm.artifact_ingress_license
      ? t("dmTab.licenses") + dm.artifact_ingress_license.join(", ")
      : undefined,
  };
}

const AddButton = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  color: "white",
  height: "24px",
  paddingLeft: "6px",
  borderRadius: "12px",
  backgroundColor: theme.palette.primary.main,
}));

export const DMTabPanel: FC<SearchDMTabContentProps> = ({
  onSelect = () => {},
  selectMultiple,
  selectedList,
  instanceId,
  ownerId,
  canExpandContent,
}) => {
  const { t } = useTranslation("common");
  const { error } = useMessenger();
  const { state } = useStore();
  const expandFetchAbortRef = useRef<AbortController | null>(null);
  const mappingRequestIdRef = useRef(0);
  const [lastUpdated, setLastUpdated] = useState<number>(Date.now());

  const queryCache = useLookup<PaginationResult<DMSearchResult>>();
  const [dmType, setDmType] = useState<string>("*");
  const [onlyOpenLicenses, setOnlyOpenLicenses] = useState<boolean>(false);
  const [includeNoPictures, setIncludeNoPictures] = useState<boolean>(false);
  const [filterByOwner, setFilterByOwner] = useState<boolean>(false);
  const [expandedContent, setExpandedContent] = useState<Array<ExpandedDmMedia>>([]);
  const [mappedSelectedItemsIds, setMappedSelectedItemsIds] = useState<Array<string>>([]);

  const [searchInput, setSearchInput] = useState<string>("");
  const [currentQuery, setCurrentQuery] = useState<string>("");
  const debouncedSetSearchTerms = useDebounce<string>(500, (t) => {
    setCurrentQuery(t || "");
    setLastUpdated(Date.now());
  });

  useEffect(() => {
    if (searchInput.length !== 1) {
      debouncedSetSearchTerms(searchInput);
    }
  }, [debouncedSetSearchTerms, searchInput]);

  const updateDmType = (value: string): void => {
    setDmType(value);
    setLastUpdated(Date.now());
  };

  const updateLicenseType = (value: boolean): void => {
    setOnlyOpenLicenses(value);
    setLastUpdated(Date.now());
  };

  const onToggleNoPictures = (checked: boolean) => {
    setIncludeNoPictures(checked);
    setLastUpdated(Date.now());
  };

  const onToggleFilterByOwner = (checked: boolean) => {
    setFilterByOwner(checked);
    setLastUpdated(Date.now());
  };

  async function createMedia(item: DMSearchResult | DMImage, artifactId: string): Promise<Media | null> {
    const isDmSearchResult = "artifact_uniqueId" in item && item.artifact_uniqueId;

    let dominantColor: string | undefined = undefined;
    let imgDimensions: string | undefined = undefined;

    const getImageDimensionsAndDominantColor = async (imgUrl: string) => {
      const imageFile = await getFileFromUrl(imgUrl, imgUrl);
      const _imgDimensions = await getImageDimensions(imageFile);
      const _dominantColor = await getDominantColor(imageFile, imgDimensions);
      return [_imgDimensions, _dominantColor];
    };
    if (isDmSearchResult && item.thumbnail_src) {
      const imgUrl = cleanDmUrl(item.thumbnail_src);
      [imgDimensions, dominantColor] = await getImageDimensionsAndDominantColor(imgUrl);
    } else {
      const imgUrl = cleanDmUrl((item as DMImage).url);
      [imgDimensions, dominantColor] = await getImageDimensionsAndDominantColor(imgUrl);
    }

    return await Api.createMedia({
      media_type: MediaType.DM,
      src: isDmSearchResult
        ? item.thumbnail_src || `https://digitaltmuseum.org/${item.artifact_uniqueId}`
        : cleanDmUrl((item as DMImage).url),
      name: isDmSearchResult ? item.artifact_ingress_title : `${item.mediaId}_${(item as DMImage).imageId}`,
      thumbnail_src: isDmSearchResult
        ? item.thumbnail_src || `https://digitaltmuseum.org/${item.artifact_uniqueId}`
        : cleanDmUrl((item as DMImage).url),
      filename: isDmSearchResult ? item.artifact_defaultMediaIdentifier : item.mediaId || "no filename",
      identifier: isDmSearchResult ? item.artifact_uniqueId : item.mediaId,
      owner_id: ownerId,
      application_instance_id: instanceId,
      additional_metadata: {
        digitaltMuseum_artifactId: artifactId,
      },
      dimension: imgDimensions,
      dominant_color: dominantColor,
    })
      .fetchDirect(null)
      .then((media) => {
        if (!media) {
          error("kioForm.widgets.MediaSelectorField.selectionError");
          return null;
        }
        return media;
      });
  }

  async function getMediaForItem(item: DMSearchResult | DMImage): Promise<Media | null> {
    const isDmSearchResult = "artifact_uniqueId" in item && item.artifact_uniqueId;
    const identifier = isDmSearchResult ? item.artifact_uniqueId : item.mediaId;
    if (!identifier || !ownerId || !instanceId) return null;
    return await Api.getMediaUnique({
      identifier: identifier,
      owner_id: ownerId,
      application_instance_id: instanceId,
      media_type: MediaType.DM,
    })
      .fetch()
      .then(([value, error, _]) => {
        if (value) return value;
        if (
          error &&
          ((typeof error !== "string" && error.exception !== "KIONotFoundException") ||
            (typeof error === "string" && error !== "KIONotFoundException"))
        )
          console.error(error);

        return null;
      });
  }

  const onItemClick = async (item: DMSearchResult | DMImage) => {
    const isDmSearchResult = "artifact_uniqueId" in item && item.artifact_uniqueId;
    if (!isDmSearchResult) {
      if (!selectMultiple && item.mediaId) {
        let savedMedia = await getMediaForItem(item);
        const artifactId =
          expandedContent?.find((content) => content.items.some((i) => i.mediaId === item.mediaId))?.parentItemId ?? "";
        if (!savedMedia) savedMedia = await createMedia(item, artifactId);
        if (savedMedia) onSelect(savedMedia);
        else error("kioForm.widgets.MediaSelectorField.selectionError");
      }
      return;
    }

    // item is parent DMSearchResult
    if ((item.artifact_pictureCount ?? 0) > 1 && canExpandContent) {
      if (expandedContent && expandedContent.some((content) => content.parentItemId === item.artifact_uniqueId)) {
        expandFetchAbortRef.current?.abort();
        expandFetchAbortRef.current = null;
        setExpandedContent((prev) => prev.filter((content) => content.parentItemId !== item.artifact_uniqueId));
        return;
      } else {
        expandFetchAbortRef.current?.abort();
        const controller = new AbortController();
        expandFetchAbortRef.current = controller;

        try {
          const response = await fetch(
            `https://api.dimu.org/api/artifact?unique_id=${item?.artifact_uniqueId}&mapping=simple_json&api.key=demo`,
            { signal: controller.signal }
          );

          if (!response.ok) {
            throw new Error(`DM artifact fetch failed: ${response.status}`);
          }

          const data = await response.json();
          if (!data?.images || controller.signal.aborted) return;

          const tempImages = {
            parentItemId: item.artifact_uniqueId,
            items: data.images.map((img: DMImage) => ({
              ...img,
              key: img.mediaId,
              imageURL: img.url.replace("http:", "https:"),
              url: img.url.replace("http:", "https:"),
              title: img.mediaId || "",
              sourceItem: {
                ...img,
                key: img.mediaId,
                imageURL: img.url.replace("http:", "https:"),
                url: img.url.replace("http:", "https:"),
                title: img.mediaId || "",
              },
            })),
          } as ExpandedDmMedia;

          if (
            !!item?.artifact_defaultMediaIdentifier &&
            tempImages?.items?.map((item) => item?.mediaId)?.includes(item?.artifact_defaultMediaIdentifier)
          ) {
            // Change the media with equal ID to contain parent item values,
            // so they can be used when creating media and when the image is
            // displayed in the expanded content list with correct title, image etc.

            let defaultMediaIndex = tempImages.items.findIndex(
              (img) => img.mediaId === item.artifact_defaultMediaIdentifier
            );
            if (defaultMediaIndex > -1) {
              tempImages.items[defaultMediaIndex] = {
                ...tempImages.items[defaultMediaIndex],
                sourceItem: item,
                key: item.artifact_uniqueId || item.identifier_id || item.artifact_uuid || "no key",
                imageURL: item.thumbnail_src,
                url: item.thumbnail_src ?? "",
                title: item.artifact_ingress_title || item.artifact_uniqueId || item.identifier_id || "no title",
                artifact_ingress_title: item.artifact_ingress_title,
                thumbnail_src: item.thumbnail_src,
                artifact_defaultMediaIdentifier: item.artifact_defaultMediaIdentifier,
                artifact_uniqueId: item.artifact_uniqueId,
              };
            }
          }

          setExpandedContent((prev) => {
            if (prev.some((content) => content.parentItemId === tempImages.parentItemId)) return prev;
            return [...prev, tempImages];
          });
        } catch (err) {
          if (err instanceof DOMException && err.name === "AbortError") return;
          error("kioForm.widgets.MediaSelectorField.selectionError");
        } finally {
          if (expandFetchAbortRef.current === controller) {
            expandFetchAbortRef.current = null;
          }
        }
      }
    } else {
      if (!selectMultiple && (item.artifact_uniqueId || item.mediaId)) {
        let savedMedia: Media | null = await getMediaForItem(item);
        if (!savedMedia) {
          savedMedia = await createMedia(item, item.artifact_uniqueId || "");
        }
        if (savedMedia) {
          onSelect(savedMedia);
        } else {
          error("kioForm.widgets.MediaSelectorField.selectionError");
        }
      }
    }
  };

  const getItems = async (page: number, page_size: number): Promise<PaginationResult<DMSearchResult>> => {
    const query = currentQuery;
    const cacheKey =
      `${page}-${page_size}-${query}-${dmType}-${onlyOpenLicenses}-${includeNoPictures}-${filterByOwner}`.toLowerCase();
    const cachedPage = queryCache.getItem(cacheKey);
    setExpandedContent([]);
    if (cachedPage) {
      return cachedPage;
    }
    const result = await Api.searchDM({
      page,
      page_size,
      query,
      dm_type: dmType,
      open_licenses_only: onlyOpenLicenses,
      include_no_pictures: includeNoPictures,
      ...(!!filterByOwner ? { filter_by_owner: state?.cmsContextInstance?.owner_dm_identifier } : {}),
    }).fetchDirect({
      page,
      page_size,
      items: [],
      count: 0,
      total_count: 0,
    });
    queryCache.setItem(cacheKey, result);
    return result;
  };

  async function handleSelectionChanged(item: DMSearchResult | DMImage) {
    const isDmSearchResult = "artifact_uniqueId" in item && item.artifact_uniqueId;
    const identifier = "artifact_uniqueId" in item ? item.artifact_uniqueId : item.mediaId;
    if (identifier) {
      let savedMedia: Media | null = await getMediaForItem(item);
      if (!savedMedia) {
        const artifactId = isDmSearchResult
          ? item.artifact_uniqueId ?? ""
          : expandedContent.find((content) => content.items.some((i) => i.mediaId === item.mediaId))?.parentItemId ??
            "";
        savedMedia = await createMedia(item, artifactId);
      }
      if (savedMedia) {
        return savedMedia;
      } else {
        error("kioForm.widgets.MediaSelectorField.selectionError");
      }
    }
    return null;
  }

  const resetPageDeps = useMemo(
    () => [currentQuery, dmType, onlyOpenLicenses, includeNoPictures, filterByOwner],
    [currentQuery, dmType, onlyOpenLicenses, includeNoPictures, filterByOwner]
  );

  useEffect(() => {
    if (!selectedList || selectedList.length === 0) {
      setMappedSelectedItemsIds([]);
      return;
    }
    let isActive = true;
    mappingRequestIdRef.current += 1;
    const requestId = mappingRequestIdRef.current;
    const request = Api.getMediaIdentifiersByIds(selectedList);
    const runIdMapper = async () => {
      try {
        const [value, fetchError] = await request.fetch();
        if (!isActive || requestId !== mappingRequestIdRef.current) {
          return;
        }
        if (value) {
          setMappedSelectedItemsIds(value.map((item) => item.identifier.toString()));
          return;
        }
        if (fetchError) {
          setMappedSelectedItemsIds(selectedList);
          return;
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setMappedSelectedItemsIds(selectedList);
      }
    };
    runIdMapper();
    return () => {
      isActive = false;
      request.abort();
    };
  }, [selectedList]);

  return (
    <Box height={"calc(100% - 56px)"}>
      <DmFilterContainer>
        <KioSelect
          label={t("dmTab.dmType")}
          onChange={(value) => updateDmType(value)}
          value={dmType}
          options={getDmTypes(t)}
          isHeaderVariant
        />
        <FormControlLabel
          control={<Switch checked={onlyOpenLicenses} onChange={(e) => updateLicenseType(e.target.checked)} />}
          label={<LabelText>{t("dmTab.licenseType")}</LabelText>}
        />
      </DmFilterContainer>
      <DataList
        listTitle={t("components.MediaSelector.AddDMContent.searchResult")}
        getItems={getItems}
        mapperFn={(dm) => mapDMSearchResultToDataListItem(dm, t)}
        externalDataChanged={lastUpdated}
        onItemClick={selectMultiple && !canExpandContent ? undefined : onItemClick}
        disableSorting
        defaultPageSize={10}
        resetPageDeps={resetPageDeps}
        onToggle={onToggleNoPictures}
        toggleLabel={"dmTab.includeNoPictures"}
        canExpandContent={canExpandContent}
        expandedContent={canExpandContent ? expandedContent : undefined}
        disableSelection={!selectMultiple}
        customToggles={
          !!state.cmsContextInstance?.owner_dm_identifier
            ? [
                {
                  onToggle: (value) => onToggleFilterByOwner(value),
                  toggleLabel: "dmTab.filterByOwner",
                  toggleDefault: false,
                },
              ]
            : []
        }
        searchProp={
          {
            query: searchInput,
            updateQuery: setSearchInput,
            placeholder: t("search.searchDM"),
          } as SearchProp
        }
        selectedItems={mappedSelectedItemsIds}
        customBatchButtons={[
          {
            tooltip: t("components.list.header.addSelected"),
            onClickAll: async (items: DataListRowInternal<DMSearchResult | DMImage>[]) => {
              const results = await Promise.all(items.map(async (i) => await handleSelectionChanged(i.sourceItem)));
              onSelect(results.filter((r): r is Media => r !== undefined && r !== null));
            },
            icon: (
              <AddButton>
                <Typography variant={"caption"} lineHeight={1}>
                  {t("generic.add")}
                </Typography>
                <Add />
              </AddButton>
            ),
            displayCondition: () => true,
          },
        ]}
      />
    </Box>
  );
};

export default DMTabPanel;
